/**
 * DriverGpsOnboarding — Phase 43.6
 *
 * Card non-bloccante (in cima a Driver Home) che invita il driver ad abilitare
 * la geolocalizzazione. Compare:
 *  - se la permission API riporta "denied" o "prompt" e non è stata granted
 *  - se l'utente non l'ha rimandato con "lo farò più tardi" (flag localStorage)
 *
 * Non blocca la navigazione: è solo una card sopra al resto della pagina.
 * Se l'utente clicca "Lo farò più tardi", la card scompare per 7 giorni.
 *
 * Props:
 *  posState: "idle" | "requesting" | "granted" | "denied" | "unavailable"
 *  onEnable: () => void  (richiama navigator.geolocation.getCurrentPosition)
 */
import { useEffect, useState } from "react";
import { MapPin, X, ShieldCheck, Loader2 } from "lucide-react";

const STORAGE_KEY = "dapnow_driver_gps_onboarding_dismissed";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 giorni

function isDismissed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function DriverGpsOnboarding({ posState, onEnable }) {
  const [dismissed, setDismissed] = useState(() => isDismissed());
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    setSupported(typeof navigator !== "undefined" && !!navigator.geolocation);
  }, []);

  // Mostra solo quando la posizione NON è ancora concessa o è stata negata.
  // Se browser senza geolocation → niente onboarding (è il caso "unavailable").
  if (!supported) return null;
  if (posState === "granted") return null;
  if (posState === "unavailable") return null;
  if (dismissed && posState !== "denied") return null;
  // Se denied: mostra sempre (l'utente potrebbe voler ritentare),
  // ma una dismiss esplicita la nasconde per 7 giorni.
  if (dismissed && posState === "denied") return null;

  const handleLater = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  const requesting = posState === "requesting";

  return (
    <div
      className="relative rounded-2xl border border-dapnow-blue/20 bg-gradient-to-br from-dapnow-blue-soft/60 to-white p-4 sm:p-5 flex items-start gap-3"
      data-testid="driver-gps-onboarding"
    >
      <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-dapnow-blue text-white">
        <MapPin className="h-5 w-5" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-display text-base font-bold tracking-tight">
          Abilita la posizione per le consegne
        </p>
        <p className="text-xs text-dapnow-muted mt-1 leading-snug">
          DapNow usa la tua posizione durante le consegne per aggiornare il cliente
          e migliorare il tracking. Senza posizione il tracking sarà limitato e gli
          ordini non saranno ordinati per vicinanza.
        </p>
        {posState === "denied" && (
          <p className="text-[11px] text-amber-800 mt-2 flex items-start gap-1.5">
            <ShieldCheck className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>
              Hai negato la posizione. Tocca il lucchetto/scudo nella barra del browser →
              <strong> Posizione → Consenti</strong>, poi premi "Abilita posizione".
            </span>
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onEnable}
            disabled={requesting}
            data-testid="driver-gps-enable-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-dapnow-ink text-white text-xs font-bold px-3.5 py-1.5 hover:bg-dapnow-blue transition disabled:opacity-60"
          >
            {requesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <MapPin className="h-3 w-3" />}
            {requesting ? "Attendi…" : "Abilita posizione"}
          </button>
          <button
            type="button"
            onClick={handleLater}
            data-testid="driver-gps-later-btn"
            className="text-xs font-semibold text-dapnow-muted hover:text-dapnow-ink"
          >
            Lo farò più tardi
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={handleLater}
        aria-label="Chiudi"
        data-testid="driver-gps-dismiss-btn"
        className="text-dapnow-muted hover:text-dapnow-ink transition"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
